import { GitHubRepository } from "@back4app2/sdk/lib/Back4app2";
import { ReactComponent as ErrorIcon } from "../../assets/images/error-icon.svg";
import { ReactComponent as GhostIcon } from "../../assets/images/ghost-icon.svg";
import { ReactComponent as GithubIcon } from "../../assets/images/github-logo.svg";
import { ReactComponent as B4AIconSVG } from "../../assets/images/b4a-sqaure-glow-logo.svg";

import styles from './IntegrateGithub.module.css';
import Button from "../Button";
import { timeFromNow } from "../../utils/dates";
import MessageContainer from "./MessageContainer/MessageContainer";
import Skeleton from "../Skeleton";

interface IntegrateGithubProps {
  githubRepos: GitHubRepository[] | undefined,
  isImporting: Boolean,
  errorMsg: string | undefined,
  onImport: (repository: GitHubRepository) => void,
  filterText: string | undefined;
  isLoadingRepository: boolean
}

/**
 * The repository list as the user has narrowed it.
 *
 * Exported because the search box that supplies `filterText` lives one level
 * up now, in the wizard, and it reports how many results it found. Two copies
 * of this predicate would agree until the day one of them learned to match on
 * something else, and the count would then quietly disagree with the list
 * right below it.
 *
 * The user's text goes into a RegExp unescaped, which is how this has always
 * behaved: a stray `(` throws, so the call is guarded rather than the pattern
 * rewritten — changing the matching semantics is a different decision from
 * moving the function.
 */
export const filterRepositories = (
  repositories: GitHubRepository[],
  filterText: string | undefined
): GitHubRepository[] => {
  if (!filterText) {
    return repositories;
  }

  let regex: RegExp;

  try {
    regex = new RegExp(filterText, 'ig');
  } catch (e) {
    // Mid-typing `foo(` is not an error the user should see — it is a search
    // that has not finished being written. Match nothing until it parses.
    return [];
  }

  return repositories.filter((repository) => repository.fullName.match(regex));
};

const IntegrateGithub = ({ githubRepos, isImporting, errorMsg, onImport, filterText, isLoadingRepository }: IntegrateGithubProps) => {

  if (isImporting) {
    return <>
      <div className="border-b border-b-regal-blue px-7 py-20">
        <div className="flex justify-center items-center overflow-visible h-6 mb-4 z-[-1]">
          <GithubIcon width="24px" height="24px" />
          <div className="flex space-x-2 ml-5">
            <span className="w-1 h-1 rounded bg-[#D9D9D9] animate-dot-flashing"></span>
            <span className="w-1 h-1 rounded bg-[#D9D9D9] animate-[dot-flashing_1s_infinite_0.5s_alternate] "></span>
            <span className="w-1 h-1 rounded bg-[#D9D9D9] animate-[dot-flashing_1s_infinite_1s_alternate] "></span>
          </div>
          <B4AIconSVG width="76px" height="76px" />
        </div>
        <div className="font-sora font-semibold text-lg leading-140 text-center">Loading repositories</div>
      </div>
    </>
  }

  if (isLoadingRepository) {
    return <>
      {Array(4).fill(0).map((_value, index) => <GithubRepositoryItem key={index} loading={isLoadingRepository} onImport={onImport} />)}
      <MessageContainer
        layout="row"
        msgTitle=""
        msgDesc="Not seeing a repository? Grant access on GitHub and it shows up here instantly."
        showButton={true}
        buttonText="Edit GitHub permissions"
        buttonAction="EDIT"
      />
    </>
  }

  if (errorMsg) {
    return <MessageContainer 
      msgTitle={errorMsg} 
      primaryIcon={<ErrorIcon />} 
      msgDesc={"Please try again."} 
      showButton={true}
      buttonText="Import GitHub Repo"
      buttonAction="IMPORT"
    />
  }

  if (!githubRepos || githubRepos.length === 0) {
    return <MessageContainer 
      msgTitle={"Nothing here, yet!"} 
      primaryIcon={<GhostIcon />}
      showButton={true}
      buttonText="Import GitHub Repo"
      buttonAction="IMPORT"
    />
  }

  const filteredRepositoryList = filterRepositories(githubRepos, filterText);

  return (
    <>
      {filteredRepositoryList.map(repo => {
        return(
          <GithubRepositoryItem key={repo.id}
            loading={isLoadingRepository} 
            repositoryData={{...repo}}
            onImport={onImport} 
          />
        )}
      )}
      <MessageContainer
        layout="row"
        msgTitle=""
        msgDesc="Not seeing a repository? Grant access on GitHub and it shows up here instantly."
        showButton={true}
        buttonText="Edit GitHub permissions"
        buttonAction="EDIT"
      />
    </>
  )
}

export default IntegrateGithub

const GithubRepositoryItem = (props: { loading: boolean, repositoryData?: GitHubRepository, onImport: (repository: GitHubRepository) => void}) => {
  let { loading, repositoryData, onImport } = props;

  // Select is the only thing that selects. The row carries a repository name
  // that is itself a link to GitHub, so a row-wide click would sit on top of
  // it — and the two do opposite things: one leaves for another site, the
  // other commits you to a deploy. A row where the whole surface acts, except
  // for one word in the middle that acts differently, is a row people
  // mis-click exactly once and then stop trusting.
  return <div
    className="flex justify-between items-center gap-4 py-[1.125rem] px-[1.875rem] border-b border-b-card-line"
  >
    <div className="flex items-center min-w-0 flex-1">
      <GithubIcon width="19px" height="19px" className={`flex-none ${loading ? 'text-white/[0.48]' : 'text-white'}`} />
      <span className="min-w-0 ml-3">
        <span className="block text-[0.9375rem] font-medium leading-140 text-light-blue truncate">{loading ? <Skeleton className="h-4 w-80" /> : <a href={`https://www.github.com/${repositoryData?.fullName}`} target="_blank" rel="noopener noreferrer" className="hover:underline hover:underline-offset-2">{repositoryData?.fullName}</a>}</span>
        {!loading && (
          <span className="flex items-center gap-2 mt-1 text-xs leading-140 text-text-faint">
            {repositoryData?.defaultBranch && <span className="truncate">{repositoryData.defaultBranch}</span>}
            {repositoryData?.defaultBranch && repositoryData?.pushedAt && <span className="w-[3px] h-[3px] rounded-full bg-text-faint flex-none"></span>}
            {repositoryData?.pushedAt && <span className="whitespace-nowrap">{timeFromNow(repositoryData.pushedAt)}</span>}
          </span>
        )}
      </span>
    </div>
    <div className="flex items-center justify-end flex-none">
      {loading ? <Skeleton className="h-4 w-20" /> : <Button onClick={() => { repositoryData && onImport(repositoryData)}} type="primary" className={styles.importBtn}>Select</Button>}
    </div>
  </div>
}